import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, Backdrop
} from '@mui/material';
import { Add, Edit, Schedule } from '@mui/icons-material';

const StaffRoster: React.FC = () => {
  const [staffSchedules, setStaffSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [editedSchedule, setEditedSchedule] = useState<any>({});

  useEffect(() => {
    fetchStaffSchedules();
  }, []);

  const fetchStaffSchedules = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/schedules');
      if (response.ok) {
        const schedules = await response.json();
        const schedulesWithAppointments = schedules;
        setStaffSchedules(schedulesWithAppointments);
      }
    } catch (error) {
      console.error('Error fetching staff schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Busy': return 'warning';
      case 'Off': return 'error';
      default: return 'default';
    }
  };

  const handleEditSchedule = (staff: any) => {
    setSelectedStaff(staff);
    setEditedSchedule(staff.schedule);
    setIsDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/schedules/${selectedStaff.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule: editedSchedule })
      });
      
      if (response.ok) {
        fetchStaffSchedules();
        setIsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  const handleScheduleChange = (day: string, field: string, value: string) => {
    setEditedSchedule((prev: any) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box
      sx={{
        background: `
          linear-gradient(135deg, rgba(255, 248, 245, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%),
          url('https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Schedule sx={{ fontSize: 40, color: '#E91E63' }} />
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Staff Duty Roster
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage staff schedules and availability
          </Typography>
        </Box>
      </Box>

      {/* Staff Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {staffSchedules.map((staff) => (
          <Grid item xs={12} md={6} lg={3} key={staff.id}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    {staff.name}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleEditSchedule(staff)}
                  >
                    Edit
                  </Button>
                </Box>
                <Chip
                  label={staff.role}
                  size="small"
                  sx={{ 
                    mb: 2,
                    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'capitalize'
                  }}
                />
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Specialties:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {staff.specialties.map((specialty: string, index: number) => (
                      <Chip 
                        key={index} 
                        label={specialty} 
                        size="small" 
                        sx={{ 
                          fontSize: '0.7rem', 
                          height: 20,
                          background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                          color: 'white',
                          '& .MuiChip-label': { px: 1 }
                        }} 
                      />
                    ))}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Today's Appointments: {staff.appointments}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Weekly Schedule Table */}
      <Card
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Weekly Schedule Overview
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Staff Member</strong></TableCell>
                  {days.map((day) => (
                    <TableCell key={day} align="center">
                      <strong>{day}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {staffSchedules.map((staff) => (
                  <TableRow key={staff.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {staff.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {staff.role}
                        </Typography>
                      </Box>
                    </TableCell>
                    {days.map((day) => (
                      <TableCell key={day} align="center">
                        <Box>
                          <Chip
                            label={staff.schedule[day].status}
                            color={getStatusColor(staff.schedule[day].status) as any}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          {staff.schedule[day].start && (
                            <Typography variant="caption" display="block">
                              {staff.schedule[day].start} - {staff.schedule[day].end}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Schedule Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Schedule - {selectedStaff?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {days.map((day) => (
              <Grid item xs={12} sm={6} md={4} key={day}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {day}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={editedSchedule[day]?.status || 'Available'}
                    size="small"
                    sx={{ mb: 1 }}
                    onChange={(e) => handleScheduleChange(day, 'status', e.target.value)}
                  >
                    <MenuItem value="Available">Available</MenuItem>
                    <MenuItem value="Busy">Busy</MenuItem>
                    <MenuItem value="Off">Off</MenuItem>
                  </TextField>
                  {editedSchedule[day]?.status !== 'Off' && (
                    <>
                      <TextField
                        fullWidth
                        label="Start Time"
                        type="time"
                        value={editedSchedule[day]?.start || '09:00'}
                        size="small"
                        sx={{ mb: 1 }}
                        onChange={(e) => handleScheduleChange(day, 'start', e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="End Time"
                        type="time"
                        value={editedSchedule[day]?.end || '17:00'}
                        size="small"
                        onChange={(e) => handleScheduleChange(day, 'end', e.target.value)}
                      />
                    </>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSchedule}>
            Save Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffRoster;
